import { RawEventKind, RawEventOfKind } from '@fullstory/recording-api';

export type FSDialogState = 'CLOSED' | 'OPEN';

/**
 * An HTMLDialog element can be in one of 3 states denoted by FSDialogState above.
 *   One can determine 'CLOSED' by the absence of an 'open' attribute on the element.
 *   Differentiating between 'OPEN' requires (chrome/ff >= 105). One
 *   must check the presence of an 'open' attribute and whether the dialog matches
 *   'modal:dialog'. In older browsers, opening a model is denoted by an internal selector
 *   and calling .matches() on 'modal:dialog' throws an error 🤦‍♂️. For these browsers, we
 *   assume the most likely use case which is opening a modal.
 *
 *   Furthermore, calling .showModal() on an 'open' dialog throws an error, so we must always
 *   reset the modal (see note on reset() below).
 */
export class FSDialog {
  private _state: FSDialogState = 'CLOSED';
  private _isModal: boolean = false;

  setState(state: FSDialogState) {
    this._state = state;
  }

  setIsModal(isModal: boolean) {
    this._isModal = isModal;
  }

  callShow(dialog: HTMLDialogElement) {
    const isOpen = getIsOpen(dialog);
    if (this._isModal) {
      if (isOpen && tryGetIsModal(dialog)) {
        return;
      }
      reset(dialog, isOpen);
      dialog.showModal();
    } else if (!isOpen) {
      reset(dialog, isOpen);
      dialog.show();
    }
  }

  callClose(dialog: HTMLDialogElement) {
    const isOpen = getIsOpen(dialog);
    if (!isOpen) {
      return;
    }

    dialog.close();
  }

  flush(dialog: HTMLDialogElement | undefined) {
    if (!dialog) {
      return;
    }

    switch (this._state) {
      case 'CLOSED': {
        this.callClose(dialog);
        break;
      }
      case 'OPEN': {
        this.callShow(dialog);
        break;
      }
    }
  }
}

/** returns undefined for MUT_ATTR events that are not dialog related */
export const getDialogState = (
  evt: RawEventOfKind<'DIALOG' | 'MUT_ATTR'>,
): FSDialogState | undefined => {
  switch (evt.Kind) {
    case RawEventKind.DIALOG: {
      return evt.Args[1] ? 'OPEN' : 'CLOSED';
    }
    case RawEventKind.MUT_ATTR: {
      if (evt.Args[1] !== 'open') {
        return undefined;
      }
      return evt.Args[2] === null ? 'CLOSED' : 'OPEN';
    }
  }
};

const tryGetIsModal = (dialog: HTMLDialogElement) => {
  try {
    return dialog.matches('dialog:modal');
  } catch (e) {
    return true;
  }
};

const getIsOpen = (dialog: HTMLDialogElement) => dialog.getAttribute('open') === '';

/**
 * If we try to call methods (specifically showModal) on a dialog with
 *   an open attribute, an error will be thrown. Calling reset (removing 'open') prevents
 *   this error. This allows the dialog to move seamlessly from 'OPEN' to in model mode in
 *   the cases where a seek skip causes this transition. NOTE: calling .show() or .showModal()
 *   adds the 'open' attribute.
 */
const reset = (dialog: HTMLDialogElement, isOpen: boolean) => {
  if (isOpen) {
    dialog.removeAttribute('open');
  }
};
